import React, {ChangeEvent} from 'react';
import './BasisForPrinting.scss';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import {ReactComponent as RadioActiveIcon} from '../../../../src//assets/images/radio-active-icon.svg';
import {ReactComponent as RadioInactiveIcon} from '../../../../src/assets/images/radio-inactive-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../src/assets/images/radio-enable-icon.svg';
import {t} from 'i18next';
import classNames from 'classnames';
import {
  setPrintIssueSettingCode,
  setSaveBatchPrint,
  setSelectedProductionList,
  setSelectedProductionSet,
} from '../../../redux/slice/batchPrintSlice';
import {useDispatch, useSelector} from 'react-redux';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTransferList, {
  KeyValue,
} from '../../common/arr-transfer-list/ArrTransferList';
import {
  useGetProductionListBySetId,
  useGetProductionSets,
} from '../../../hooks/documentPrinting/useDocumentPrinting';

const radioOptions = [
  {
    value: 'SKIPPRINTBUTFOLDER',
    label: t('batchPrint.basisForPrinting.viewableFile'),
  },
  {
    value: 'IGNOREPRINT',
    label: t('batchPrint.basisForPrinting.priorProductions'),
  },
];
const BasisForPrinting = () => {
  const batchPrint = useSelector((state: any) => state.batchPrint);

  const printIssueSettingCode =
    batchPrint?.basisForPrinting?.printIssueSettingCode;
  const selectedProductionSet =
    batchPrint?.basisForPrinting?.selectedProductionSet;
  const selectedProductionList =
    batchPrint?.basisForPrinting?.selectedProductionList;

  const dispatch = useDispatch();

  const {data: getProductionSetsData} = useGetProductionSets();

  const {
    data: getProductionListBySetId,
    refetch: refetchGetProductionListBySetId,
  } = useGetProductionListBySetId(selectedProductionSet?.id);

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedAction = event.target.value;
    dispatch(setPrintIssueSettingCode(selectedAction));
    dispatch(setSaveBatchPrint({printIssueSettingCode: selectedAction}));
    dispatch(setSelectedProductionSet({id: '', name: 'Select'}));
    dispatch(setSelectedProductionList([]));
    dispatch(setSaveBatchPrint({productionSetID: 0}));
    dispatch(setSaveBatchPrint({productionIDs: []}));
    dispatch(setSaveBatchPrint({exportFileNameFieldID: 0}));
    dispatch(setSaveBatchPrint({exportFileNameFieldName: ''}));
    dispatch(
      setSaveBatchPrint({
        isNewSlipSheet: false, // This sets dropdown to "Use Slip Sheets from Prior Productions"
        slipSheetDetails: [],
        includeMediaFilePlaceHolder: selectedAction !== 'IGNOREPRINT',
        isExcelFileSkipped: selectedAction !== 'IGNOREPRINT',
        includeExcelFilePlaceHolder: selectedAction !== 'IGNOREPRINT',
        includeOtherExceptionFileTypesPlaceHolder:
          selectedAction !== 'IGNOREPRINT',
      })
    );
  };

  const isRadioEnabled = true;
  const handleDropdownChange = (value: any) => {
    dispatch(setSelectedProductionSet(value));
    dispatch(setSelectedProductionList([]));
    dispatch(setSaveBatchPrint({productionSetID: value.id}));
    dispatch(setSaveBatchPrint({productionIDs: []}));
    refetchGetProductionListBySetId();
  };

  const handleSelectedProductionList = (
    event: ChangeEvent<HTMLInputElement>,
    field: KeyValue
  ) => {
    const isChecked = event.target.checked;

    const selectedList = selectedProductionList;

    let updatedList;

    if (isChecked) {
      updatedList = [...selectedList, field];
    } else {
      updatedList = selectedList.filter((item) => item.id !== field.id);
    }
    dispatch(setSelectedProductionList(updatedList));
    dispatch(
      setSaveBatchPrint({productionIDs: updatedList?.map((i) => i.id) || []})
    );
  };

  return (
    <Box className="basis-for-printing-container">
      <Box className="source-selection-header ">
        <Typography
          className="typo-text subhead2_size"
          data-testid={`basis-for-printing-title`}
          tabIndex={0}
        >
          {t('batchPrint.basisForPrinting.title')}
        </Typography>
        <ArrTooltipWrapper
          title={t('batchPrint.basisForPrinting.title')}
          closeTooltipOnEscPress={true}
          trigger={
            <InfoIcon
              fontSize="small"
              className="info-icon"
              data-testid="add-user-title-info"
              tabIndex={0}
              aria-label='info-icon'
            />
          }
        >
          <p tabIndex={0}>{t('batchPrint.basisForPrinting.titleInfo')}</p>
        </ArrTooltipWrapper>
      </Box>
      <Box className="source-selection-body">
        <Box className="body-header">
          <Typography
            className="typo-text body2_size"
            data-testid={`basis-for-printing-description`}
            tabIndex={0}
          >
            {t('batchPrint.basisForPrinting.description')}
          </Typography>
          <Box>
            <RadioGroup
              row={false}
              value={printIssueSettingCode}
              onChange={handleActionChange}
              aria-label={t('batchPrint.basisForPrinting.description')}
            >
              {radioOptions.map(({value, label}, index) => {
                const isSelected = printIssueSettingCode === value;

                return (
                  <FormControlLabel
                   data-testid={`action-radio-label-${value}`}
                    key={value}
                    value={value}
                    control={
                      <Radio
                        slotProps={{
                          input: {
                            'data-testid': `radio-${value}`,
                             'tabIndex': printIssueSettingCode === value ? 0 : -1,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                        disabled={false}
                        icon={<RadioEnableIcon data-testid={`action-radio-icon-enabled-${value}`}/>}
                        checkedIcon={<RadioActiveIcon  data-testid={`action-radio-active-icon--${value}`}/>}
                       
                      />
                    }
                    label={
                      <span
                        className={classNames(
                          'bulk-radio-label typo-text body3_size',
                          {
                            disabled: !isRadioEnabled,
                            active: isSelected && isRadioEnabled,
                            inactive: !isSelected && isRadioEnabled,
                          }
                        )}
                        data-testid={`basis-for-printing-option-${index}-label`}
                      >
                        {label}
                      </span>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Box>
        </Box>

        {printIssueSettingCode === 'IGNOREPRINT' && (
          <Box className="tree-container">
            <Box className="dropdown-container">
              <Typography
                className="typo-text subhead4_size content-title"
                data-testid={`basis-for-printing-select-production-set`}
                tabIndex={0}
              >
                {t('batchPrint.basisForPrinting.selectProductionSet')}
              </Typography>
              <Box className="dropdown">
                <ArrSingleSelectDropdown
                  dropdownLabel={t(
                    'batchPrint.basisForPrinting.selectProductionSet'
                  )}
                  dropdownItems={getProductionSetsData?.productionSet?.map(
                    (i) => ({
                      id: i.productionSetID,
                      name: i.productionSetLabel,
                    })
                  )}
                  selectedValues={selectedProductionSet}
                  onSelectionChange={handleDropdownChange}
                  sizeVariant="medium"
                  dataTestId={'asd-basis-access-control'}
                  selectRestProps={{
                    id: t('userManagement.assignUsers.domain')
                      ? 'domain-dropdown'
                      : 'project-dropdown',
                    name: t('userManagement.assignUsers.domain')
                      ? 'domain-dropdown'
                      : 'project-dropdown',
                    'aria-label': t('userManagement.assignUsers.domain')
                      ? 'domain-dropdown'
                      : 'project-dropdown',
                    'aria-haspopup': 'listbox',
                  }}
                  tabIndex={1}
                />
              </Box>
            </Box>
            <Box className="list-container" tabIndex={0}>
              <ArrTransferList
                showCheckbox={true}
                selectedList={selectedProductionList}
                displayedList={
                  getProductionListBySetId?.map((i) => ({
                    id: i.productionID,
                    label: i.productionName,
                  })) || [
                    {
                      id: 1,
                      label: 'label',
                    },
                  ]
                }
                onChange={handleSelectedProductionList}
                checkboxDataTestId="basis-for-printing-production-transfer-list-checkbox"
                listItemContainerClass="select-production-list"
                title={t('batchPrint.basisForPrinting.selectProduction')}
                disable={false}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BasisForPrinting;
